package fyi.moko.hotpl.domain.repository

import fyi.moko.hotpl.domain.model.PhotoRef
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceVisitSummary
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.service.DuplicateKey
import kotlinx.coroutines.flow.Flow

import fyi.moko.hotpl.domain.model.SyncQueueItem
import fyi.moko.hotpl.domain.model.SyncState

/**
 * Single source of truth for places and their visits. The implementation lives in
 * the data layer and is backed by the local database (offline-first).
 */
interface PlaceRepository {
    fun observePlaces(): Flow<List<Place>>
    fun observeVisitSummaries(): Flow<List<PlaceVisitSummary>>
    fun observePlace(id: String): Flow<Place?>
    suspend fun getPlace(id: String): Place?
    suspend fun getPlaceIncludingDeleted(id: String): Place?
    suspend fun hasPlaceWithNormalizedName(
        normalizedName: String,
        excludePlaceId: String? = null,
    ): Boolean

    /** Creates or updates a place. A brand-new place also records its first visit. */
    /**
     * [firstVisitAt]: 새 장소의 암묵적 첫 방문 시각. null이면 지금. 사진에서 읽은 촬영
     * 시각이 있을 때만 채워진다 — 어제 찍은 사진을 오늘 넣어도 방문은 어제가 되도록.
     */
    suspend fun savePlace(place: Place, firstVisitAt: Long? = null): String
    suspend fun savePlaceFromRemote(place: Place)
    suspend fun toggleLiked(id: String)
    suspend fun deletePlace(id: String)

    fun observeVisits(placeId: String): Flow<List<Visit>>

    /** Every visit across all places, oldest first — for app-open recall derivation. */
    fun observeAllVisits(): Flow<List<Visit>>
    suspend fun addVisit(
        placeId: String,
        visitedAt: Long,
        note: String? = null,
        emotion: String? = null,
        imageFileName: String? = null,
        photos: List<PhotoRef> = emptyList(),
    ): String
    suspend fun getVisitIncludingDeleted(id: String): Visit?
    suspend fun saveVisitFromRemote(visit: Visit)
    suspend fun deleteVisit(id: String)

    /**
     * Read-modify-write update of a visit that's already saved — bumps [Visit.version] and
     * [Visit.updatedAt] and marks it `PENDING_UPSERT` so the edit reaches other devices, the
     * same shape [savePlace] gives places. Used by
     * [fyi.moko.hotpl.domain.usecase.CorrectPhotoUseCase] to edit a visit's photo list after
     * the visit itself was already logged.
     *
     * Default no-op, matching [countPlacesOwnedBy]'s reasoning: only one call site edits a
     * saved visit, so a fake that doesn't implement this just behaves as if the edit never
     * landed.
     */
    suspend fun updateVisit(visit: Visit): Unit = Unit
    suspend fun getPendingVisitSyncItems(limit: Int): List<Visit>
    suspend fun markVisitSynced(id: String, syncState: SyncState)
    suspend fun brandLocalVisitsWithUser(uid: String)
    suspend fun strandedVisitOwnerIds(currentUid: String): List<String>
    suspend fun rebrandVisitOwners(fromUid: String, toUid: String)

    /**
     * Every photo file name referenced by a live (not soft-deleted) place or visit, in no
     * particular order and without duplicates. Used once per device to queue uploads for
     * photos saved before photo sync existed (see `SyncCoordinator.backfillPhotoUploads`).
     * Defaulted to empty so fakes that never deal with photos need not implement it.
     */
    suspend fun photoFileNamesInUse(): List<String> = emptyList()

    suspend fun getPendingSyncItems(): List<SyncQueueItem>
    suspend fun getPendingSyncItems(limit: Int): List<SyncQueueItem>
    suspend fun deleteSyncItem(queueId: Long)
    suspend fun markPlaceSynced(id: String, syncState: SyncState)
    suspend fun brandLocalPlacesWithUser(uid: String)

    /**
     * Owner ids carried by local places that aren't [currentUid]. Non-empty only after an account
     * switch signed in but failed to carry the previous session's places over (see
     * [fyi.moko.hotpl.domain.usecase.AccountSwitchIncompleteException]); those places are stranded
     * — they would be pushed under the new account with a stale owner, and a pull can overwrite
     * them. [fyi.moko.hotpl.data.service.SyncCoordinator] adopts them onto the current user before
     * it attaches its listener.
     */
    suspend fun strandedOwnerIds(currentUid: String): List<String>

    /**
     * Re-owns places currently owned by [fromUid] onto [toUid], marking them
     * [SyncState.PENDING_UPSERT] and enqueueing UPSERTs. Used when an anonymous
     * session switches into an existing account (the uid changes) so local temp
     * data follows the new identity instead of being orphaned under the old uid.
     *
     * [reservedIds] are the place ids the target account already owns remotely. A local place
     * whose id is in that set is given a fresh id (its visits and queue entry follow), because a
     * place id is also its Firestore document id — leaving the collision in place would let the
     * incoming remote dataset overwrite the local place on the next pull. The two places are
     * unrelated, so they must coexist rather than merge.
     *
     * Since schema v9 ids are UUIDs, so only places carried over from before then can collide.
     *
     * [duplicateKeys] maps a [DuplicateKey] (name + coordinates) to the id of an existing remote
     * place in the target account that matches it. A local place matching one of these is not
     * given its own new document: it adopts the existing remote id instead (visits and queue
     * entry follow, same as the [reservedIds] path) so an account switch doesn't create a visible
     * duplicate of a place the target account already has. The local row left under that id is a
     * placeholder — `version = 0` guarantees the next pull's LWW check always prefers the
     * existing remote data over it, and it is never enqueued for push (there is nothing new to
     * upload; the real content lives in the doc it just adopted).
     */
    suspend fun rebrandPlaces(
        fromUid: String,
        toUid: String,
        reservedIds: Set<String> = emptySet(),
        duplicateKeys: Map<DuplicateKey, String> = emptyMap(),
    )

    /**
     * Resets [placeId]'s version to 1 and bumps `updatedAt` to now, keeping it
     * `PENDING_UPSERT`. Used by the push recovery path when a write fails because the
     * place's version is stale for the Firestore doc's create/update boundary — e.g. a
     * place rebranded before the version-reset fix (3528c35) carried version > 1 into a
     * new doc path, which the prod rules reject on create.
     */
    suspend fun resetPlaceForRemoteCreate(placeId: String)

    /**
     * Count of non-deleted places currently owned by [uid]. Used by [fyi.moko.hotpl.domain.usecase.LinkAccountUseCase]
     * to decide whether an account switch needs to ask the user what to do with the previous
     * session's local data — asking is pointless when there is nothing to lose.
     *
     * Default 0: like [DuplicateKey]-based dedup, this only matters to the one call site that
     * asks; a fake or platform that doesn't implement it just behaves as if there's nothing to
     * ask about.
     */
    suspend fun countPlacesOwnedBy(uid: String): Int = 0

    /**
     * Permanently deletes every place (and, via cascade, their visits) currently owned by [uid],
     * bypassing the normal soft-delete/tombstone-push path. Used when the user chooses to
     * discard an anonymous session's local places rather than carry them into the account they
     * just switched to: [uid] is an abandoned session that will never authenticate again, so
     * there is no one left to push a tombstone to, and no reason to keep a soft-deleted row
     * around forever.
     *
     * Default no-op, matching [countPlacesOwnedBy]'s reasoning.
     */
    suspend fun discardPlacesOwnedBy(uid: String) = Unit
}
